import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AutoplayOverlay } from './AutoplayOverlay';
import { VideoFeatures } from '@/store/videoFeatures';
import { useVideoFeatures } from '@/store/videoFeatures';
import { AutoplayOverlay as AutoplayOverlayType } from '@/store/videoFeatures/types/autoplay';
import {
  BREAKPOINT_CONSTRAINTS,
  getBreakpoint,
  clampAutoplayOverlayLayout,
  type Breakpoint,
  type AutoplayOverlayInput,
} from '@/utils/autoplayOverlayGeometry';

// Templates que suportam redimensionamento/reposicionamento livre.
// Demais templates (fullScreen, blur, animation) ocupam a tela inteira e não
// fazem sentido serem arrastados.
// `custom` é a imagem personalizada — também redimensionável/arrastável.
const RESIZABLE_TEMPLATES = ['autoPlay', 'small', 'large', 'transparent', 'custom'];

// Posições/tamanhos padrão por template, usados como fallback quando o
// usuário ainda não arrastou/redimensionou.
const TEMPLATE_DEFAULTS: Record<string, { xPercent: number; yPercent: number; widthPercent: number }> = {
  autoPlay:    { xPercent: 50, yPercent: 50, widthPercent: 30 },
  small:       { xPercent: 50, yPercent: 12, widthPercent: 28 },
  large:       { xPercent: 50, yPercent: 50, widthPercent: 36 },
  transparent: { xPercent: 50, yPercent: 50, widthPercent: 30 },
  // Equivale à caixa fixa antiga (195px ≈ 30% de um player de 640px).
  custom:      { xPercent: 50, yPercent: 50, widthPercent: 30 },
};

// Mínimos em pixels por template — garantem que a caixa nunca fique menor que
// o conteúdo mínimo do overlay (ícone + texto + padding), impedindo aparência
// "bugada" onde o texto transborda a caixa.
const TEMPLATE_MIN_PX: Record<string, { width: number; height: number }> = {
  autoPlay:    { width: 210, height: 68 },
  small:       { width: 210, height: 40 },
  large:       { width: 200, height: 88 },
  transparent: { width: 135, height: 62 },
  // Mínimo menor que os demais: a imagem personalizada não tem texto nem ícone
  // para caber, então travar em 200px+ impediria overlays pequenos legítimos.
  custom:      { width: 80,  height: 45 },
};

// Snap guides
const SNAP_THRESHOLD = 2.5;
const SNAP_LINES_X = [5, 10, 25, 50, 75, 90, 95];
const SNAP_LINES_Y = [5, 10, 25, 50, 75, 90, 95];

function snapValue(val: number, lines: number[]): { snapped: number; guide: number | null } {
  for (const line of lines) {
    if (Math.abs(val - line) < SNAP_THRESHOLD) {
      return { snapped: line, guide: line };
    }
  }
  return { snapped: val, guide: null };
}

type HandlePosition = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const HANDLE_CURSORS: Record<HandlePosition, string> = {
  nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize',
  e: 'ew-resize', se: 'nwse-resize', s: 'ns-resize',
  sw: 'nesw-resize', w: 'ew-resize',
};

interface AutoplayResizeWrapperProps {
  features: VideoFeatures;
  onClick: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const AutoplayResizeWrapper: React.FC<AutoplayResizeWrapperProps> = ({
  features, onClick, containerRef,
}) => {
  useLanguage();
  const setFeature = useVideoFeatures((state) => state.setFeature);

  const onUpdate = useCallback((partial: Partial<AutoplayOverlayType>) => {
    const current = features.autoplayOverlay;
    if (!current) return;
    setFeature('autoplayOverlay', { ...current, ...partial });
  }, [features.autoplayOverlay, setFeature]);

  const templateId = features.autoplayOverlay?.templateId || 'autoPlay';
  const isResizable = RESIZABLE_TEMPLATES.includes(templateId);

  // Templates que não suportam redimensionamento livre (fullScreen, blur,
  // animation) renderizam direto o overlay — o wrapper não interfere em
  // pulso/borda nesses casos.
  if (!isResizable) {
    return <AutoplayOverlay features={features} onClick={onClick} />;
  }

  return (
    <ResizableOverlay
      features={features}
      onClick={onClick}
      containerRef={containerRef}
      templateId={templateId}
      onUpdate={onUpdate}
    />
  );
};

interface ResizableOverlayProps {
  features: VideoFeatures;
  onClick: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
  templateId: string;
  onUpdate: (partial: Partial<AutoplayOverlayType>) => void;
}

const ResizableOverlay: React.FC<ResizableOverlayProps> = ({
  features, onClick, containerRef, templateId, onUpdate,
}) => {
  useLanguage();
  const defaults = TEMPLATE_DEFAULTS[templateId] || TEMPLATE_DEFAULTS.autoPlay;
  const minPx = TEMPLATE_MIN_PX[templateId] ?? TEMPLATE_MIN_PX.autoPlay;

  const overlay = features.autoplayOverlay!;

  // Pulso vem do switch `havePulse` no backend (mapeado para `pulseAnimation`
  // no store). `pulseEffect` é legado — o load duplica o valor em ambos os
  // campos, mas só `pulseAnimation` é atualizado pelo switch. Se a gente
  // combinasse com OR (`pulseAnimation || pulseEffect`), o legado travaria o
  // valor em `true` e o toggle não funcionaria.
  const shouldPulse = !!overlay.pulseAnimation;

  const [selected, setSelected] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState<HandlePosition | null>(null);

  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, xPct: 0, yPct: 0 });
  const resizeStartRef = useRef({ mouseX: 0, mouseY: 0, wPct: 0, hPct: 0, xPct: 0, yPct: 0 });
  const [activeSnapX, setActiveSnapX] = useState<number | null>(null);
  const [activeSnapY, setActiveSnapY] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Track container width — usado para escolher o breakpoint (mobile/tablet/
  // desktop) que define os limites de width, height e área segura no clamp.
  // O mesmo getBreakpoint roda no AutoplayOverlay (preview) e no runner.js
  // (player), garantindo que o que o usuário arrasta no editor seja onde o
  // player vai renderizar.
  const [containerWidth, setContainerWidth] = useState<number>(
    () => containerRef.current?.getBoundingClientRect().width ?? 640,
  );
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setContainerWidth((prev) => (Math.abs(prev - w) > 0.5 ? w : prev));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  const breakpoint: Breakpoint = useMemo(() => getBreakpoint(containerWidth), [containerWidth]);
  const constraints = BREAKPOINT_CONSTRAINTS[breakpoint];

  // Layout overlay-side: se size.heightPercent vier setado (resize vertical)
  // tratamos como 'fixed' pro clamp considerar a altura real ao bater na safe
  // area. Se não, fica 'auto' e o clamp usa estimatedAutoHeightPercent do
  // breakpoint. Isso mantém o tipo AutoplayOverlaySize estável (heightMode:
  // 'auto') mas dá ao clamp a informação certa.
  const overlayForClamp = useMemo<AutoplayOverlayInput>(() => {
    const size = overlay.size;
    const hasFixedHeight = size?.heightPercent != null;
    return {
      position: overlay.position ?? { xPercent: defaults.xPercent, yPercent: defaults.yPercent },
      size: size
        ? {
            widthPercent: size.widthPercent,
            heightMode: hasFixedHeight ? 'fixed' : 'auto',
            heightPercent: size.heightPercent,
          }
        : { widthPercent: defaults.widthPercent, heightMode: 'auto' },
    };
  }, [overlay.position, overlay.size, defaults]);

  // Usa o MESMO clamp que o player vai usar — assim a posição renderizada
  // aqui no editor é idêntica à do embed na produção.
  const clampedLayout = useMemo(
    () => clampAutoplayOverlayLayout(overlayForClamp, breakpoint),
    [overlayForClamp, breakpoint],
  );
  const pos = { xPercent: clampedLayout.xPercent, yPercent: clampedLayout.yPercent };
  const displayWidthPercent = clampedLayout.widthPercent;
  const hasExplicitHeight = overlay.size?.heightPercent != null;

  // Clique fora deseleciona
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setSelected(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getContainerRect = useCallback(() => {
    return containerRef.current?.getBoundingClientRect() || { width: 640, height: 360, left: 0, top: 0 };
  }, [containerRef]);

  // --- DRAG ---
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Clique em handle não inicia drag (já é um resize)
    if ((e.target as HTMLElement).dataset?.handle) return;
    e.preventDefault();
    e.stopPropagation();
    setSelected(true);
    setDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      xPct: pos.xPercent,
      yPct: pos.yPercent,
    };
  }, [pos]);

  // --- RESIZE ---
  const handleResizeStart = useCallback((handle: HandlePosition, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizing(handle);

    // Captura altura inicial: se o usuário já resizou verticalmente antes,
    // usa o valor salvo; senão mede do DOM (altura atual baseada no conteúdo).
    // Isso torna o primeiro arraste diagonal suave — a alça começa exatamente
    // no canto visível do overlay.
    let initialHPct = overlay.size?.heightPercent;
    if (initialHPct == null && wrapperRef.current) {
      const rect = getContainerRect();
      initialHPct = (wrapperRef.current.offsetHeight / rect.height) * 100;
    }

    resizeStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      wPct: displayWidthPercent,
      hPct: initialHPct ?? 20,
      xPct: pos.xPercent,
      yPct: pos.yPercent,
    };
  }, [overlay.size, pos, displayWidthPercent, getContainerRect]);

  useEffect(() => {
    if (!dragging && !resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = getContainerRect();

      if (dragging) {
        const dx = e.clientX - dragStartRef.current.mouseX;
        const dy = e.clientY - dragStartRef.current.mouseY;
        const dxPct = (dx / rect.width) * 100;
        const dyPct = (dy / rect.height) * 100;

        // Roda pelo MESMO clamp do player. A posição livre (sem snap) é a
        // saída do clamp, que respeita min/max de safe area por breakpoint.
        const probe = clampAutoplayOverlayLayout(
          {
            ...overlayForClamp,
            position: {
              xPercent: dragStartRef.current.xPct + dxPct,
              yPercent: dragStartRef.current.yPct + dyPct,
            },
          },
          breakpoint,
        );
        const rawX = probe.xPercent;
        const rawY = probe.yPercent;

        const sx = snapValue(rawX, SNAP_LINES_X);
        const sy = snapValue(rawY, SNAP_LINES_Y);
        setActiveSnapX(sx.guide);
        setActiveSnapY(sy.guide);

        onUpdate({
          position: {
            xPercent: Math.round(sx.snapped * 10) / 10,
            yPercent: Math.round(sy.snapped * 10) / 10,
          },
        });
      }

      if (resizing) {
        const s = resizeStartRef.current;
        const dxPx = e.clientX - s.mouseX;
        const dyPx = e.clientY - s.mouseY;

        // Cada eixo normalizado pela sua própria dimensão do container — dy
        // pela altura, dx pela largura. Assim, mover o cursor 10px na vertical
        // mapeia corretamente para % de altura (não de largura).
        const dxPctW = (dxPx / rect.width) * 100;
        const dyPctH = (dyPx / rect.height) * 100;

        // O elemento é posicionado pelo centro (translate -50%/-50%), então
        // mover uma borda 1% aumenta a dimensão total em 2% (cresce pros dois
        // lados simetricamente).
        let dWidth = 0;
        let dHeight = 0;
        switch (resizing) {
          case 'e':  dWidth =  dxPctW * 2;                                break;
          case 'w':  dWidth = -dxPctW * 2;                                break;
          case 'n':  dHeight = -dyPctH * 2;                               break;
          case 's':  dHeight =  dyPctH * 2;                               break;
          case 'ne': dWidth =  dxPctW * 2; dHeight = -dyPctH * 2;         break;
          case 'se': dWidth =  dxPctW * 2; dHeight =  dyPctH * 2;         break;
          case 'nw': dWidth = -dxPctW * 2; dHeight = -dyPctH * 2;         break;
          case 'sw': dWidth = -dxPctW * 2; dHeight =  dyPctH * 2;         break;
        }

        // Mínimos em % derivados dos mínimos em pixels (template) — garantem
        // que o resize não deixe a caixa menor que o conteúdo mínimo. Os
        // limites máximos vêm do breakpoint (BREAKPOINT_CONSTRAINTS).
        const minWPctFromPx = (minPx.width / rect.width) * 100;
        const minHPctFromPx = (minPx.height / rect.height) * 100;
        const effectiveMinWPct = Math.max(constraints.minWidthPercent, minWPctFromPx);
        const effectiveMinHPct = Math.max(constraints.minHeightPercent, minHPctFromPx);

        const newWRaw = s.wPct + dWidth;
        const newHRaw = s.hPct + dHeight;
        const hasNewHeight = dHeight !== 0;

        // Passa pelo clamp do player pra ficar idêntico ao runtime. Se houve
        // resize vertical, marca como heightMode:'fixed' pra o clamp respeitar
        // a altura ao recalcular a safe area de posição.
        const reclampInput: AutoplayOverlayInput = {
          ...overlayForClamp,
          position: { xPercent: s.xPct, yPercent: s.yPct },
          size: hasNewHeight
            ? { widthPercent: newWRaw, heightMode: 'fixed', heightPercent: newHRaw }
            : { ...overlayForClamp.size, widthPercent: newWRaw },
        };
        const reclamp = clampAutoplayOverlayLayout(reclampInput, breakpoint);

        // Aplica o mínimo de px (template-specific) por cima do clamp do
        // breakpoint. Garante que a caixa nunca encolha além do conteúdo.
        const finalW = Math.max(effectiveMinWPct, reclamp.widthPercent);
        const finalH = hasNewHeight
          ? Math.max(effectiveMinHPct, reclamp.heightPercent)
          : null;

        const sizePayload: AutoplayOverlayType['size'] = hasNewHeight
          ? {
              widthPercent: Math.round(finalW * 10) / 10,
              heightMode: 'auto',
              heightPercent: Math.round((finalH ?? 0) * 10) / 10,
            }
          : {
              ...(overlay.size ?? {}),
              widthPercent: Math.round(finalW * 10) / 10,
              heightMode: 'auto',
            };

        onUpdate({
          position: {
            xPercent: Math.round(reclamp.xPercent * 10) / 10,
            yPercent: Math.round(reclamp.yPercent * 10) / 10,
          },
          size: sizePayload,
        });
      }
    };

    const handleMouseUp = () => {
      setDragging(false);
      setResizing(null);
      setActiveSnapX(null);
      setActiveSnapY(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, resizing, overlay.size, getContainerRect, onUpdate, breakpoint, constraints, minPx, overlayForClamp]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!dragging && !resizing) {
      setSelected(true);
    }
  }, [dragging, resizing]);

  const handles: HandlePosition[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

  const getHandleStyle = (h: HandlePosition): React.CSSProperties => {
    const isCorner = ['nw', 'ne', 'se', 'sw'].includes(h);
    const isTop = h === 'n';
    const sz = isTop ? 14 : isCorner ? 12 : 10;
    const offset = -(sz / 2);

    const base: React.CSSProperties = {
      position: 'absolute',
      width: sz,
      height: sz,
      background: '#FFFFFF',
      border: '2px solid #1D4FFF',
      borderRadius: '50%',
      cursor: HANDLE_CURSORS[h],
      zIndex: 30,
      boxShadow: isTop
        ? '0 0 0 3px rgba(29, 79, 255, 0.2), 0 2px 8px rgba(0,0,0,0.2)'
        : '0 0 0 2px rgba(29, 79, 255, 0.12), 0 1px 4px rgba(0,0,0,0.15)',
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    };

    const positions: Record<HandlePosition, React.CSSProperties> = {
      nw: { top: offset, left: offset },
      n:  { top: offset, left: '50%', transform: 'translateX(-50%)' },
      ne: { top: offset, right: offset },
      e:  { top: '50%', right: offset, transform: 'translateY(-50%)' },
      se: { bottom: offset, right: offset },
      s:  { bottom: offset, left: '50%', transform: 'translateX(-50%)' },
      sw: { bottom: offset, left: offset },
      w:  { top: '50%', left: offset, transform: 'translateY(-50%)' },
    };
    return { ...base, ...positions[h] };
  };

  const tooltipText = hasExplicitHeight
    ? `${Math.round(displayWidthPercent)}% × ${Math.round(clampedLayout.heightPercent)}%  ·  x: ${Math.round(pos.xPercent)}, y: ${Math.round(pos.yPercent)}`
    : `${Math.round(displayWidthPercent)}%  ·  x: ${Math.round(pos.xPercent)}, y: ${Math.round(pos.yPercent)}`;
  const showSafeArea = selected || dragging || !!resizing;

  const safeAreaGuides = showSafeArea && containerRef.current ? createPortal(
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 15 }}>
      <div style={{
        position: 'absolute',
        top: `${constraints.safeAreaPercent}%`,
        left: `${constraints.safeAreaPercent}%`,
        right: `${constraints.safeAreaPercent}%`,
        bottom: `${constraints.safeAreaPercent}%`,
        border: '1px dashed rgba(29, 79, 255, 0.35)',
        borderRadius: 6,
      }} />
      {[
        { top: `${constraints.safeAreaPercent}%`, left: `${constraints.safeAreaPercent}%`, transform: 'translate(-50%, -50%)' },
        { top: `${constraints.safeAreaPercent}%`, right: `${constraints.safeAreaPercent}%`, transform: 'translate(50%, -50%)' },
        { bottom: `${constraints.safeAreaPercent}%`, left: `${constraints.safeAreaPercent}%`, transform: 'translate(-50%, 50%)' },
        { bottom: `${constraints.safeAreaPercent}%`, right: `${constraints.safeAreaPercent}%`, transform: 'translate(50%, 50%)' },
      ].map((style, i) => (
        <div key={i} style={{
          position: 'absolute', ...style,
          width: 6, height: 6,
          background: 'rgba(29, 79, 255, 0.5)',
          borderRadius: '50%',
        }} />
      ))}
      <div style={{
        position: 'absolute',
        top: `calc(${constraints.safeAreaPercent}% + 4px)`,
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(29, 79, 255, 0.75)',
        color: '#fff',
        fontSize: 9,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        letterSpacing: '0.03em',
      }}>
        {translateInterfaceText("Área segura")}</div>
    </div>,
    containerRef.current
  ) : null;

  // IMPORTANTE: a UI de edição (hover hint, selection border, handles, snap
  // guides) fica em INSET de -6px ou mais pra não colidir visualmente com a
  // borda branca do usuário (1px) quando `whiteBorder` estiver ativo.

  return (
    <>
      {safeAreaGuides}
      <div
        ref={wrapperRef}
        style={{
          position: 'absolute',
          left: `${pos.xPercent}%`,
          top: `${pos.yPercent}%`,
          transform: 'translate(-50%, -50%)',
          width: `${displayWidthPercent}%`,
          height: hasExplicitHeight ? `${clampedLayout.heightPercent}%` : undefined,
          minWidth: `${minPx.width}px`,
          minHeight: hasExplicitHeight ? `${minPx.height}px` : undefined,
          zIndex: 20,
          cursor: selected ? (dragging ? 'grabbing' : 'grab') : 'pointer',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        {/* Hover hint — afastado da borda do overlay para não conflitar com
            a borda branca (whiteBorder) do usuário */}
        {hovering && !selected && (
          <div style={{
            position: 'absolute',
            inset: -6,
            borderRadius: 14,
            border: '1.5px dashed rgba(29, 79, 255, 0.4)',
            pointerEvents: 'none',
            zIndex: 25,
          }} />
        )}

        {/* Selection border — também afastada da borda branca */}
        {selected && (
          <div style={{
            position: 'absolute',
            inset: -6,
            borderRadius: 14,
            border: '1.5px solid rgba(29, 79, 255, 0.7)',
            boxShadow: '0 0 0 1px rgba(29, 79, 255, 0.15), 0 0 20px rgba(29, 79, 255, 0.12)',
            pointerEvents: 'none',
            zIndex: 25,
          }} />
        )}

        {/* Snap guide lines (durante drag) */}
        {dragging && activeSnapX !== null && (
          <div style={{
            position: 'absolute',
            left: `calc(${((activeSnapX - pos.xPercent) / displayWidthPercent) * 100 + 50}%)`,
            top: '-300%', bottom: '-300%',
            width: 1, background: 'rgba(29, 79, 255, 0.6)',
            pointerEvents: 'none', zIndex: 24,
          }}>
            <div style={{
              position: 'absolute', top: 0, left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(29, 79, 255, 0.85)', color: '#fff',
              fontSize: 9, fontWeight: 700, padding: '1px 5px',
              borderRadius: 3, whiteSpace: 'nowrap',
            }}>{activeSnapX}%</div>
          </div>
        )}
        {dragging && activeSnapY !== null && (
          <div style={{
            position: 'absolute',
            top: `calc(${((activeSnapY - pos.yPercent) / (displayWidthPercent * 0.5625)) * 100 + 50}%)`,
            left: '-300%', right: '-300%',
            height: 1, background: 'rgba(29, 79, 255, 0.6)',
            pointerEvents: 'none', zIndex: 24,
          }}>
            <div style={{
              position: 'absolute', left: 0, top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(29, 79, 255, 0.85)', color: '#fff',
              fontSize: 9, fontWeight: 700, padding: '1px 5px',
              borderRadius: 3, whiteSpace: 'nowrap',
            }}>{activeSnapY}%</div>
          </div>
        )}

        {/* Crosshair central quando selecionado (fora de drag) */}
        {selected && !dragging && (
          <>
            <div style={{
              position: 'absolute', left: '50%', top: '-200%', bottom: '-200%',
              width: 1, background: 'rgba(29, 79, 255, 0.2)',
              pointerEvents: 'none', zIndex: 24,
            }} />
            <div style={{
              position: 'absolute', top: '50%', left: '-200%', right: '-200%',
              height: 1, background: 'rgba(29, 79, 255, 0.2)',
              pointerEvents: 'none', zIndex: 24,
            }} />
          </>
        )}

        {/* Handles de resize */}
        {selected && handles.map((h, i) => (
          <div
            key={h}
            data-handle={h}
            style={{
              ...getHandleStyle(h),
              animation: `autoplayHandlePop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 0.025}s both`,
            }}
            onMouseDown={(e) => handleResizeStart(h, e)}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLDivElement;
              const baseTransform = (getHandleStyle(h).transform as string) || '';
              el.style.transform = baseTransform ? `${baseTransform} scale(1.35)` : 'scale(1.35)';
              el.style.boxShadow = '0 0 0 4px rgba(29, 79, 255, 0.25), 0 2px 12px rgba(29,79,255,0.3)';
              el.style.background = '#1D4FFF';
              el.style.borderColor = '#1D4FFF';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLDivElement;
              el.style.transform = (getHandleStyle(h).transform as string) || '';
              el.style.boxShadow = getHandleStyle(h).boxShadow as string;
              el.style.background = '#FFFFFF';
              el.style.borderColor = '#1D4FFF';
            }}
          />
        ))}

        {/* Tooltip de dimensão durante drag/resize */}
        {(dragging || resizing) && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 18px)', left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(15, 15, 25, 0.88)',
            backdropFilter: 'blur(12px)', color: '#fff',
            fontSize: 11, fontWeight: 600,
            fontFamily: "'Inter', system-ui, sans-serif",
            letterSpacing: '0.02em', padding: '5px 12px',
            borderRadius: 8, whiteSpace: 'nowrap', zIndex: 40,
            pointerEvents: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.06)',
          }}>
            {tooltipText}
            <div style={{
              position: 'absolute', bottom: -4, left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 8, height: 8,
              background: 'rgba(15, 15, 25, 0.88)',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }} />
          </div>
        )}

        {/* Conteúdo do overlay
            - pointerEvents:none garante que o mousedown cai no wrapper (para
              drag), não no overlay interno.
            - O pulso é aplicado AQUI (não no AutoplayOverlay) porque o wrapper
              externo já tem o translate(-50%,-50%); aplicar um scale aqui evita
              conflito de transforms.
            - Amplitude 1.1 — mesma magnitude de `pulseScale` no AutoplayOverlay
              (consistente com o resto do codebase, não mais fraco como estava).
            - O pulso pausa durante drag/resize pra não "fugir" do cursor. */}
        <div
          style={{
            pointerEvents: 'none',
            width: '100%',
            height: hasExplicitHeight ? '100%' : undefined,
            overflow: 'hidden',
            borderRadius: '10px',
            animation: (shouldPulse && !dragging && !resizing)
              ? 'autoplayPulse 2500ms ease-in-out infinite'
              : undefined,
          }}
        >
          <AutoplayOverlay
            features={features}
            onClick={() => {}}
            isEditing
            stretchHeight={hasExplicitHeight}
          />
        </div>

        <style>{`
          @keyframes autoplayHandlePop {
            from { opacity: 0; transform: scale(0); }
            to { opacity: 1; }
          }
          @keyframes autoplayPulse {
            0%   { transform: scale(1);   }
            50%  { transform: scale(1.1); }
            100% { transform: scale(1);   }
          }
        `}</style>
      </div>
    </>
  );
};
